import {useEffect} from "react";
import { Dispatch } from "redux";
import { useSelector } from "react-redux";
import {Navigate, Route, Routes} from "react-router-dom";
import posthog from "posthog-js";

import NoMatch from "./NoMatch";
import ApiInfo from "./ApiInfo";
import Home from "../containers/Home";
import About from "./About";
import Contact from "./Contact";
import Pricing from "./Pricing";
import Login from "../containers/Login";
import Logout from "../containers/Logout";
import Signup from "../containers/Signup";
import GoogleCallback from "./GoogleCallback";
import ForgotPassword from "../containers/ForgotPassword";
import StockQueryPage from "../containers/StockQueryPage";
import ViewFeatured from "../containers/ViewFeatured";
import Featured from "../containers/Featured";
import TopStocks from "../containers/TopStocks";
import { ClassicPatterns } from "./ClassicPatterns";
import { PatternDetail } from "./PatternDetail";
import { Feed } from "./Feed";
import Account from "./Account";
import Terms from "./Terms";
import Privacy from "./Privacy";
import WelcomeModal from "./WelcomeModal";
import TrackRecord from "./TrackRecord";
import Watchlist from "./Watchlist";
import AdminDashboard from "./AdminDashboard";
import AdminTrends from "./AdminTrends";
import EarningsPage from "./EarningsPage";
import Game from "./Game";
import DrawShapePage from "./DrawShapePage";
import TickerTimeframes from "./TickerTimeframes";
import { SignalScreener } from "./SignalScreener";

interface Props {
  checkLoginStatus: () => (dispatch: Dispatch<AuthenticationAction>, getState: () => RootReducerState) => void
}

export default function App(props: Props) {
  const { checkLoginStatus } = props

  const user = useSelector((state: RootReducerState) => state.authentication.user);
  const loggedIn = useSelector((state: RootReducerState) => state.authentication.loggedIn);

  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  // Tie PostHog events to the logged-in user (fresh login OR a restored session via
  // checkLoginStatus) and stamp the plan as a person property so every metric can be
  // segmented Pro vs free. Logout calls posthog.reset() to clear it. Without this,
  // events are anonymous-device-scoped and the Pro/free funnel can't be analyzed.
  useEffect(() => {
    if (loggedIn && user && !user.anonymous && user.username) {
      const isPro = (user.groups ?? []).includes("pro");
      posthog.identify(user.username, {
        email: user.email ?? undefined,
        plan: isPro ? "pro" : "free",
      });
    }
  }, [loggedIn, user]);

  return (
    <>
      <WelcomeModal />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/featured" element={<Featured />} />
        <Route path="/patterns/:ticker/:patternType/:startDate" element={<PatternDetail />} />
        <Route path="/patterns" element={<ClassicPatterns />} />
        <Route path="/top-stocks" element={<TopStocks />} />
        <Route path="/ticker/:ticker" element={<TickerTimeframes />} />
        <Route path="/ticker" element={<TickerTimeframes />} />
        <Route path="/screener" element={<SignalScreener />} />
        <Route path="/view-featured" element={<ViewFeatured />} />
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/callback" element={<GoogleCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/dashboard" element={<StockQueryPage />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/alerts" element={<Navigate to="/feed?tab=alerts" replace />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/account" element={<Account />} />
        <Route path="/track-record" element={<TrackRecord />} />
        <Route path="/watchlist" element={<Watchlist />} />
        <Route path="/earnings" element={<EarningsPage />} />
        <Route path="/game" element={<Game />} />
        <Route path="/draw" element={<DrawShapePage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/trends" element={<AdminTrends />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/developers" element={<ApiInfo />} />
        <Route path="/docs" element={<Contact topic="Docs" />} />
        <Route path="/blog" element={<Contact topic="Blog" />} />
        <Route path="/team" element={<Contact topic="Team" />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Contact topic="Careers" />} />
        <Route path="*" element={<NoMatch />} />
      </Routes>
    </>
  );
}
